import type { QuoteRequestInput, SSERoutesEvent, SSEDoneEvent, SSEErrorEvent } from "./types";
import { BASE_URL, API_KEY } from "./client";

export interface QuoteStream {
  onRoutes(cb: (event: SSERoutesEvent) => void): QuoteStream;
  onDone(cb: (event: SSEDoneEvent) => void): QuoteStream;
  onError(cb: (error: SSEErrorEvent | Error) => void): QuoteStream;
  close(): void;
}

export function createQuoteStream(params: QuoteRequestInput): QuoteStream {
  let routesCb: ((e: SSERoutesEvent) => void) | null = null;
  let doneCb: ((e: SSEDoneEvent) => void) | null = null;
  let errorCb: ((e: SSEErrorEvent | Error) => void) | null = null;

  let closed = false;
  let controller: AbortController | null = null;

  async function connect() {
    controller = new AbortController();
    try {
      const res = await fetch(`${BASE_URL}/v1/quote/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(API_KEY ? { "x-api-key": API_KEY } : {}),
        },
        body: JSON.stringify(params),
        signal: controller.signal,
      });

      if (!res.ok || !res.body) {
        throw new Error(`Request failed with status ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (!closed) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let boundary: number;
        while ((boundary = buffer.indexOf("\n\n")) !== -1) {
          const rawEvent = buffer.slice(0, boundary);
          buffer = buffer.slice(boundary + 2);
          handleSSE(rawEvent);
        }
      }
    } catch (err) {
      const isAbort = err instanceof DOMException && err.name === "AbortError";
      if (!closed && !isAbort) {
        errorCb?.(err instanceof Error ? err : new Error("Stream failed"));
      }
    }
  }

  function handleSSE(chunk: string) {
    let event = "";
    let data = "";

    for (const line of chunk.split("\n")) {
      if (line.startsWith("event:")) {
        event = line.slice("event:".length + 1).trim();
      } else if (line.startsWith("data:")) {
        data += (data ? "\n" : "") + line.slice("data:".length + 1);
      }
    }

    if (!event || !data) return;

    try {
      const parsed = JSON.parse(data);
      if (event === "routes") routesCb?.(parsed as SSERoutesEvent);
      else if (event === "done") {
        doneCb?.(parsed as SSEDoneEvent);
        stream.close();
      } else if (event === "error") {
        errorCb?.(parsed as SSEErrorEvent);
        stream.close();
      }
    } catch {
      errorCb?.(new Error("Failed to parse SSE message"));
      stream.close();
    }
  }

  const stream: QuoteStream = {
    onRoutes(cb) {
      routesCb = cb;
      return stream;
    },
    onDone(cb) {
      doneCb = cb;
      return stream;
    },
    onError(cb) {
      errorCb = cb;
      return stream;
    },
    close() {
      closed = true;
      controller?.abort();
      controller = null;
    },
  };

  connect();
  return stream;
}
